import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import stateCityData from "../../../../data/states.json";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaMapMarkerAlt,
  FaUsers,
  FaCheck,
  FaTimes,
  FaGlobe,
  FaBuilding,
  FaHashtag,
  FaUserCheck,
  FaSpinner,
  FaArrowLeft,
  FaSave,
  FaFilter,
  FaSearch,
  FaMicrophone
} from "react-icons/fa";

const PaidConferenceAdminModify = () => {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    allStates: false,
    state: [],
    city: [],
    pincode: "",
    reportersIds: [],
  });


  const [cities, setCities] = useState([]);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const states = Object.keys(stateCityData);
  const [reporters, setReporters] = useState([]); // Start with empty array
  const location = useLocation();

  const [conferenceId, setConferenceId] = useState(null);
  const [conferenceData, setConferenceData] = useState(null);
  const [selectedReporters, setSelectedReporters] = useState([]);

  // Popup and loader states
  const [showPopup, setShowPopup] = useState(false);
  const [confirmModify, setConfirmModify] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);

  useEffect(() => {
    if (location.state) {
      if (location.state.conferenceId) {
        setConferenceId(location.state.conferenceId);
      }
      if (location.state.conferenceData) {
        setConferenceData(location.state.conferenceData);
      }
    }
  }, [location]);

  // Fetch conference details if only ID is provided
  useEffect(() => {
    const fetchConferenceDetails = async () => {
      if (conferenceId && !conferenceData) {
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          if (response.data.success) {
            setConferenceData(response.data.data);
          }
        } catch (error) {
          console.error("Failed to fetch conference details:", error);
          toast.error("Failed to load conference details");
        }
      }
    };
    fetchConferenceDetails();
  }, [conferenceId, conferenceData]);

  // Fetch reporters from API
  useEffect(() => {
    const fetchReporters = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/admin/get/all/reporter`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        setReporters(response.data.reporters);
      } catch (error) {
        console.error("Failed to fetch reporters:", error);
        setReporters([]);
      }
    };
    fetchReporters();
  }, []);

  // Update cities when state changes
  useEffect(() => {
    if (form.state.length > 0) {
      // Collect all cities from selected states
      let allCities = [];
      form.state.forEach((st) => {
        allCities = [...allCities, ...(stateCityData[st] || [])];
      });
      setCities([...new Set(allCities)]);
    } else {
      setCities([]);
    }
  }, [form.state]);

  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    if (name === "allStates") {
      setForm((prev) => ({
        ...prev,
        allStates: checked,
        state: [],
        city: [],
        pincode: "",
        reportersIds: [], // Clear reporter selection when allStates is checked
      }));
    } else if (name === "state") {
      setForm((prev) => {
        const newStates = checked
          ? [...prev.state, value]
          : prev.state.filter((s) => s !== value);
        return {
          ...prev,
          state: newStates,
          city: prev.city.filter((c) =>
            newStates.some((st) => (stateCityData[st] || []).includes(c))
          ), // Remove cities not in selected states
        };
      });
    } else if (name === "city") {
      setForm((prev) => ({
        ...prev,
        city: checked
          ? [...prev.city, value]
          : prev.city.filter((c) => c !== value),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: If state is selected, city must be selected
    if (form.state.length > 0 && form.city.length === 0) {
      toast.error("Please select at least one city after selecting states");
      return;
    }

    // Validation: If city is selected, at least one reporter must be selected
    if (form.city.length > 0 && form.reportersIds.length === 0) {
      toast.error("Please select at least one reporter after selecting cities");
      return;
    }

    // Validation: If state is selected but no city, still require reporter selection
    if (form.state.length > 0 && form.city.length === 0 && form.reportersIds.length === 0) {
      toast.error("Please select at least one reporter for the selected states");
      return;
    }

    // Validation: If no targeting is selected at all
    if (!form.allStates && form.state.length === 0 && form.city.length === 0 && form.reportersIds.length === 0) {
      toast.error("Please select at least one targeting option (All States, States, Cities, or Reporters)");
      return;
    }

    setShowPopup(true);
  };

  // Filter reporters based on form selection, but always include selected reporters
  const isAnyFilterApplied =
    form.allStates ||
    form.state.length > 0 ||
    form.city.length > 0 ||
    form.pincode;

  let filteredReporters = [];
  if (!isAnyFilterApplied) {
    // Only show selected reporters if no filter is applied
    filteredReporters = reporters.filter((r) => form.reportersIds.includes(r._id));
  } else if (form.allStates) {
    filteredReporters = reporters;
  } else {
    // Filtered by state/city/pincode, but always include selected
    const filtered = reporters.filter((r) => {
      if (form.pincode) {
        return String(r.pincode) === String(form.pincode);
      } else if (form.city.length > 0) {
        return form.city.includes(r.city) && form.state.includes(r.state);
      } else if (form.state.length > 0) {
        return form.state.includes(r.state);
      }
      return false;
    });
    // Always include selected reporters
    const selected = reporters.filter((r) => form.reportersIds.includes(r._id));
    // Merge and remove duplicates
    const merged = [...filtered, ...selected];
    // Remove duplicates by _id
    filteredReporters = Array.from(
      new Map(merged.map((r) => [r._id, r])).values()
    );
  }

  // Add this handler
  const handleReporterSelect = (id) => {
    setForm((prev) => {
      const updated = prev.reportersIds.includes(id)
        ? prev.reportersIds.filter((rid) => rid !== id)
        : [...prev.reportersIds, id];
      return {
        ...prev,
        reportersIds: updated,
      }
    });
  };

  // Select all reporters
  const handleSelectAllReporters = () => {
    const availableReporterIds = filteredReporters
      .filter(reporter => {
        // Don't select reporters who are already in the conference's acceptedReporters
        const isAlreadySelected = conferenceData?.acceptedReporters?.some(
          acceptedReporter => acceptedReporter.reporterId === reporter._id
        );
        return !isAlreadySelected;
      })
      .map(reporter => reporter._id);

    setForm((prev) => ({
      ...prev,
      reportersIds: [...new Set([...prev.reportersIds, ...availableReporterIds])],
    }));
  };

  // Unselect all reporters
  const handleUnselectAllReporters = () => {
    setForm((prev) => ({
      ...prev,
      reportersIds: [],
    }));
  };

  // Add refs for dropdowns
  const stateDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        stateDropdownRef.current &&
        !stateDropdownRef.current.contains(event.target)
      ) {
        setShowStateDropdown(false);
      }
      if (
        cityDropdownRef.current &&
        !cityDropdownRef.current.contains(event.target)
      ) {
        setShowCityDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // API call function to modify conference
  const modifyConferenceAPI = async () => {
    setShowPopup(false); // First close the popup modal
    setTimeout(async () => {
      setShowApproveSpinner(true); // Then show spinner overlay (after popup closes)
      const payload = {
        action: "modified",
        note: "Conference modified with new targeting",
        selectedStates: form.state,
        adminSelectCities: form.city,
        adminSelectPincode: form.pincode,
        reporterId: form.reportersIds,
        allStates: form.allStates,
      };

      try {
        const response = await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/admin/${conferenceId}/action`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Conference modified successfully:", response);
        toast.success("Conference is successfully modified!");
        setTimeout(() => {
          setShowApproveSpinner(false);
          navigate("/admin/press-conference/paid-conference/pending");
        }, 1200);
      } catch (error) {
        setShowApproveSpinner(false);
        if (error.response && error.response.status === 500) {
          toast.error("Internal Server Error. Please try again later.");
        } else {
          toast.error("Failed to modify conference");
        }
      }
    }, 200); // 200ms delay to allow popup to close smoothly
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <ToastContainer position="top-right" autoClose={1200} />

      {/* Modern Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                <FaMicrophone className="text-2xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold">Modify Paid Conference</h1>
                <p className="text-blue-100 mt-2">Update conference targeting and reporter assignments</p>
                {conferenceData && (
                  <p className="text-blue-200 mt-1">Conference ID: {conferenceData.conferenceId}</p>
                )}
              </div>
            </div>
            <button
              onClick={() => navigate("/admin/press-conference/paid-conference/pending")}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-colors backdrop-blur-sm"
            >
              <FaArrowLeft />
              <span>Back to Conferences</span>
            </button>
          </div>
        </div>
      </div>

      {/* Spinner Overlay */}
      {showApproveSpinner && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="flex flex-col items-center p-8 bg-white rounded-2xl shadow-2xl border border-blue-200 animate-fadeIn">
            {/* Custom Tailwind Spinner */}
            <div className="relative mb-4 w-16 h-16 grid place-items-center">
              {/* Outer ring */}
              <span className="absolute inset-0 rounded-full border-4 border-transparent border-r-blue-500 animate-spin" style={{ borderWidth: "4px" }} />
              {/* Middle ring */}
              <span className="absolute inset-0 m-1 rounded-full border-4 border-transparent border-r-purple-500 animate-spin-slow" style={{ borderWidth: "4px", animationDuration: "2s" }} />
              {/* Inner ring */}
              <span className="absolute inset-0 m-2 rounded-full border-4 border-transparent border-r-indigo-500 animate-spin-slower" style={{ borderWidth: "4px", animationDuration: "3s" }} />
            </div>
            <span className="text-lg font-semibold text-blue-700 tracking-wide">
              Processing Modification...
            </span>
            <span className="text-sm text-gray-500 mt-2">
              Please wait while we update the conference.
            </span>
          </div>
        </div>
      )}

      {/* Modern Popup Modal */}
      {showPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-96 border border-gray-100">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FaEdit className="text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800">Confirm Modification</h3>
            </div>
            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="confirmModify"
                  checked={confirmModify}
                  onChange={e => setConfirmModify(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="confirmModify" className="text-gray-700 font-medium">
                  I confirm that I want to modify this conference
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="flex items-center space-x-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors"
                onClick={() => { setShowPopup(false); setConfirmModify(false); }}
                disabled={showApproveSpinner}
              >
                <FaTimes />
                <span>Cancel</span>
              </button>
              <button
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-colors ${(!confirmModify || showApproveSpinner)
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg'
                  }`}
                disabled={!confirmModify || showApproveSpinner}
                onClick={() => modifyConferenceAPI()}
              >
                <FaSave />
                <span>Modify Conference</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Form Section */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-6">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-3">
                <FaFilter />
                <span>Targeting Configuration</span>
              </h2>
              <p className="text-blue-100 mt-2">Configure conference targeting parameters</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              {/* All States Selection */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center space-x-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FaGlobe className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <label htmlFor="allStates" className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        id="allStates"
                        name="allStates"
                        checked={form.allStates}
                        onChange={handleChange}
                        className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-lg font-semibold text-gray-800">Select All States</span>
                    </label>
                    <p className="text-gray-600 mt-1">Target all states and territories</p>
                  </div>
                </div>
              </div>

              {/* State Selection */}
              <div className="space-y-4" ref={stateDropdownRef}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FaMapMarkerAlt className="text-blue-600" />
                  </div>
                  <label className="text-lg font-semibold text-gray-800">Select States</label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-left bg-white hover:border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowStateDropdown((prev) => !prev)}
                    disabled={form.allStates}
                  >
                    <div className="flex items-center justify-between">
                      <span className={form.state.length > 0 ? "text-gray-800" : "text-gray-500"}>
                        {form.state.length > 0 ? `${form.state.length} state(s) selected` : "Select State(s)"}
                      </span>
                      <FaSearch className="text-gray-400" />
                    </div>
                  </button>
                  {showStateDropdown && !form.allStates && (
                    <div className="absolute z-10 bg-white border-2 border-gray-200 rounded-xl shadow-xl w-full max-h-60 overflow-y-auto mt-2">
                      {states.map((state) => (
                        <label
                          key={state}
                          className="flex items-center px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            name="state"
                            value={state}
                            checked={form.state.includes(state)}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mr-3"
                          />
                          <span className="text-gray-700">{state}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {/* Show selected states */}
                {form.state.length > 0 && (
                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaCheck className="text-blue-600" />
                      <span className="font-medium text-blue-800">Selected States:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {form.state.map((state) => (
                        <span key={state} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {state}
                        </span>
                      ))}
                    </div>
                    {form.city.length === 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium text-yellow-800">Next Step: Select cities from the selected states</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* City Selection */}
              <div className="space-y-4" ref={cityDropdownRef}>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <FaBuilding className="text-purple-600" />
                  </div>
                  <label className="text-lg font-semibold text-gray-800">Select Cities</label>
                </div>
                <div className="relative">
                  <button
                    type="button"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl text-left bg-white hover:border-purple-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => setShowCityDropdown((prev) => !prev)}
                    disabled={form.allStates || form.state.length === 0}
                  >
                    <div className="flex items-center justify-between">
                      <span className={form.city.length > 0 ? "text-gray-800" : "text-gray-500"}>
                        {form.city.length > 0 ? `${form.city.length} city(ies) selected` : "Select City(ies)"}
                      </span>
                      <FaSearch className="text-gray-400" />
                    </div>
                  </button>
                  {showCityDropdown && !form.allStates && form.state.length > 0 && (
                    <div className="absolute z-10 bg-white border-2 border-gray-200 rounded-xl shadow-xl w-full max-h-60 overflow-y-auto mt-2">
                      {cities.map((city) => (
                        <label
                          key={city}
                          className="flex items-center px-4 py-3 hover:bg-purple-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <input
                            type="checkbox"
                            name="city"
                            value={city}
                            checked={form.city.includes(city)}
                            onChange={handleChange}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500 mr-3"
                          />
                          <span className="text-gray-700">{city}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                {/* Show selected cities */}
                {form.city.length > 0 && (
                  <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <div className="flex items-center space-x-2 mb-2">
                      <FaCheck className="text-purple-600" />
                      <span className="font-medium text-purple-800">Selected Cities:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {form.city.map((city) => (
                        <span key={city} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          {city}
                        </span>
                      ))}
                    </div>
                    {form.reportersIds.length === 0 && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
                          <span className="text-sm font-medium text-orange-800">Next Step: Select at least one reporter from the sidebar</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Pincode Selection */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <FaHashtag className="text-indigo-600" />
                  </div>
                  <label htmlFor="pincode" className="text-lg font-semibold text-gray-800">
                    Pincode
                  </label>
                </div>
                <div className="relative">
                  <input
                    type="number"
                    id="pincode"
                    name="pincode"
                    value={form.pincode}
                    onChange={handleChange}
                    placeholder="Enter 6-digit pincode"
                    className="w-full p-4 border-2 border-gray-200 rounded-xl bg-white hover:border-indigo-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={
                      form.allStates || form.state.length > 1 || form.city.length > 1 || form.reportersIds.length > 0
                    }
                    maxLength={6}
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                    <FaHashtag className="text-gray-400" />
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Enter a specific 6-digit pincode for precise targeting
                </p>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                {(() => {
                  // Check if validation conditions are met
                  const isStateSelectedButNoCity = form.state.length > 0 && form.city.length === 0;
                  const isCitySelectedButNoReporter = form.city.length > 0 && form.reportersIds.length === 0;
                  const isNoTargetingSelected = !form.allStates && form.state.length === 0 && form.city.length === 0 && form.reportersIds.length === 0;
                  const isValid = !isStateSelectedButNoCity && !isCitySelectedButNoReporter && !isNoTargetingSelected;

                  return (
                    <button
                      type="submit"
                      disabled={!isValid}
                      className={`w-full py-4 px-6 rounded-xl font-semibold text-lg shadow-lg transition-all duration-300 flex items-center justify-center space-x-3 ${isValid
                          ? "bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white hover:shadow-xl"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                    >
                      <FaSave />
                      <span>Modify Conference</span>
                    </button>
                  );
                })()}
              </div>
            </form>
          </div>
        </div>
        {/* Reporter Selection Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden sticky top-6">
            <div className="bg-gradient-to-r from-green-500 to-teal-600 p-6">
              <h2 className="text-xl font-bold text-white flex items-center space-x-3">
                <FaUsers />
                <span>Reporter Selection</span>
              </h2>
              <p className="text-green-100 mt-2">Choose specific reporters for this conference</p>
            </div>

            <div className="p-6">
              {/* Validation Alert */}
              {((form.state.length > 0 && form.city.length === 0) || (form.city.length > 0 && form.reportersIds.length === 0)) && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-red-800">
                      {form.state.length > 0 && form.city.length === 0
                        ? "Please select cities first"
                        : "Please select at least one reporter"}
                    </span>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">Available Reporters</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      {filteredReporters.length} found
                    </span>
                    <button
                      type="button"
                      onClick={handleSelectAllReporters}
                      className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full hover:bg-green-200 transition-colors"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={handleUnselectAllReporters}
                      className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full hover:bg-red-200 transition-colors"
                    >
                      Unselect All
                    </button>
                  </div>
                </div>

                {filteredReporters.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {filteredReporters.map((reporter, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                        <input
                          name="reportersIds"
                          value={reporter._id}
                          type="checkbox"
                          checked={form.reportersIds.includes(reporter._id)}
                          onChange={() => handleReporterSelect(reporter._id)}
                          className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <FaUserCheck className="text-gray-400 text-sm" />
                            <span className="font-medium text-gray-800">{reporter.name}</span>
                          </div>
                          {reporter.iinsafId && (
                            <p className="text-xs text-gray-500 mt-1">ID: {reporter.iinsafId}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="p-3 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                      <FaUsers className="text-gray-400 text-xl" />
                    </div>
                    <p className="text-gray-500 font-medium">No reporters found</p>
                    <p className="text-sm text-gray-400 mt-1">Adjust your targeting criteria</p>
                  </div>
                )}
              </div>

              {/* Selected Reporters Summary */}
              {form.reportersIds.length > 0 && (
                <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-200">
                  <div className="flex items-center space-x-2 mb-3">
                    <FaCheck className="text-green-600" />
                    <span className="font-medium text-green-800">Selected Reporters</span>
                  </div>
                  <div className="space-y-2">
                    {reporters
                      .filter(r => form.reportersIds.includes(r._id))
                      .map((reporter) => (
                        <div key={reporter._id} className="flex items-center justify-between p-2 bg-white rounded-lg">
                          <span className="text-sm font-medium text-gray-700">{reporter.name}</span>
                          <button
                            onClick={() => handleReporterSelect(reporter._id)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaidConferenceAdminModify;